import React from 'react';

import { Box } from '@material-ui/core';

import { AppToggle } from '@vyce/core/src/components/AppToggle';
import { DetailsIcon } from '@vyce/core/src/components/DetailsIcon';
import { StaffResourceOption } from '@vyce/core/src/utils/staffPermissions';

import { ScopeMode } from '../types';
import { useRolesStyles } from '../styles';
import { ResourcePicker } from './ResourcePicker';

const SCOPE_HINTS: Record<string, string> = {
  Locations: 'Which locations this user can see clock-ins, timesheets and attendance for.',
  Teams: 'Which teams this user can see and manage. Anyone outside these teams stays hidden.',
  'Pay schedules':
    'Which pay schedules this user can work with. Pay runs and payments outside these schedules stay hidden.',
};

interface Props {
  label: string;
  resources: StaffResourceOption[];
  selectedIds: string[];
  disabled?: boolean;
  onToggle: (id: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  renderItemRight?: (resourceId: string) => React.ReactNode;
  renderItemDetail?: (resourceId: string) => React.ReactNode;
}

export const scopeSummary = (label: string, selectedCount: number, total: number) => {
  const name = label.toLowerCase();
  if (total === 0) return '';
  if (selectedCount === total) return `All ${name}`;
  return `${selectedCount} of ${total} ${name}`;
};

export const ScopeSelector: React.FC<Props> = ({
  label,
  resources,
  selectedIds,
  disabled,
  onToggle,
  onSelectAll,
  renderItemRight,
  renderItemDetail,
}) => {
  const classes = useRolesStyles();
  const allSelected = resources.length > 0 && selectedIds.length === resources.length;
  // Only two modes — the API stores explicit id lists, so there is nothing to
  // express an "own only" scope with.
  const mode: ScopeMode = allSelected ? 'all' : 'selected';

  const handleModeChange = (next: ScopeMode) => {
    if (next === mode) return;
    onSelectAll(next === 'all');
  };

  return (
    <div className={classes.scopeBlock}>
      <div className={classes.scopeHeader}>
        <span className={classes.scopeTitle}>{label}</span>
        {!!SCOPE_HINTS[label] && <DetailsIcon size="14px" content={<>{SCOPE_HINTS[label]}</>} />}
        <span className={classes.scopeSummary}>
          {scopeSummary(label, selectedIds.length, resources.length)}
        </span>
        <div className={classes.scopeToggle}>
          <AppToggle
            primaryActive
            disabled={disabled}
            value={mode}
            options={[
              { value: 'all', title: `All ${label.toLowerCase()}` },
              { value: 'selected', title: 'Only selected' },
            ]}
            onChange={handleModeChange}
          />
        </div>
      </div>

      {mode === 'selected' || renderItemDetail ? (
        <Box marginTop={1}>
          <ResourcePicker
            resources={resources}
            selectedIds={selectedIds}
            disabled={disabled}
            onToggle={onToggle}
            onSelectAll={onSelectAll}
            renderItemRight={renderItemRight}
            renderItemDetail={renderItemDetail}
            errorText={
              selectedIds.length === 0
                ? `Pick at least one ${label
                    .toLowerCase()
                    .replace(/s$/, '')} — otherwise this user will see nothing in this module.`
                : undefined
            }
          />
        </Box>
      ) : (
        <div className={classes.scopeCaption}>
          {/* Not "including any added later" — the backend stores an explicit id list. */}
          {`The ${resources.length} ${label.toLowerCase()} listed: ${resources
            .map(item => item.name)
            .join(' · ')}`}
        </div>
      )}
    </div>
  );
};
